import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { App } from '@mb/App';
import { AnalyticsEventLocation } from '@mb/lib';

import { getLocaleData, getUserLang } from '@mb/lib';
import { IntlProvider, useIntl } from 'react-intl';
import useSWR from 'swr';
import { definedTranslation } from './i18n';
import {
  postFetcher,
  getSearchConfig,
  getLanguageKey,
  getUserData,
  mapToNumber,
} from './utils';
import {
  MAX_DISPLAYED_ASSETS,
  MAX_SCROLLING_ASSETS,
  ScrollingCollectionCarousel,
} from '../../../../../../MIT/Search/view/frontend/pages/ProductSearch/containers/CollectionsCarouselContainer/ScrollingCollectionCarousel';

const SpaceHolder = styled.div`
  min-height: 296px;
`;

const userLang = getUserLang();
const searchConfig = getSearchConfig();
const languageKey = getLanguageKey();
const customer = getUserData();

// works only when path begins with slash
const makeIntlPath = (path: string): string => {
  let prefix = '';
  if (ENVIRONMENT_REGION === 'EU') {
    prefix = `/${userLang}`;
  }
  return `${prefix}${path}`;
};

const onCtaClick = () => {
  window.location.href = makeIntlPath('/curatedcollections');
};

export const Collections = () => {
  const intl = useIntl();

  const labels = {
    title: intl?.formatMessage(definedTranslation.title),
    button: intl?.formatMessage(definedTranslation.button),
  };

  const { data, error, isLoading } = useSWR(
    `${searchConfig.siteUrl}/collection/query`,
    (url: string) =>
      postFetcher(url, {
        customer: {
          id: customer ? customer.customer_id : '',
          email: customer ? customer.email : '',
          projectId: mapToNumber(customer?.current_project, 0),
          country: customer ? customer.country : '',
          province: customer ? customer.state : '',
        },
        siteId: searchConfig.siteId,
        lang: languageKey,
        type: ['curated_collection'],
        subtype: ['default'],
        maxResults: MAX_SCROLLING_ASSETS,
      }),
    { revalidateOnFocus: false, suspense: false },
  );

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!data || data.results.length === 0) {
    return null;
  }

  return (
    <App>
      <SpaceHolder>
        <ScrollingCollectionCarousel
          campaigns={data.results}
          onSeeAllClick={onCtaClick}
          actionLabel={labels.button}
          isLoading={isLoading}
          title={labels.title}
          eventLocation={AnalyticsEventLocation.HOME}
          ctaPosition="left"
          slideLimit={Math.max(data.results.length, MAX_DISPLAYED_ASSETS)}
          autoStart
        />
      </SpaceHolder>
    </App>
  );
};

export const CollectionsApp = () => {
  const [langData, setLangData] = useState({
    currentLocale: 'en',
    messages: {},
  });

  useEffect(() => {
    handleLangData();
  }, []);

  const handleLangData = async () => {
    const data = await getLocaleData();
    // @ts-expect-error // TODO: fix types
    setLangData(data);
  };
  return (
    <IntlProvider
      messages={langData?.messages}
      locale={langData?.currentLocale}
      defaultLocale="en"
    >
      <Collections />
    </IntlProvider>
  );
};
